import _ from "lodash";
import { UserDataAdapter } from "../presentation/adpters/UserDataAdapter";
import * as DateUtils from "../utils/date/date";
import { FormatNumbers } from "../utils/format-numbers";
import { CNPJCPFMask, CNPJMask, maskChar } from "../utils/format/format";
import { NormalizeString } from "../utils/normalize-string/NormalizeString";
import { RolesUtils } from "../utils/roles/roles";
import { Entity } from "./Entity.abstract";

export class CompanyEntity extends Entity {
    scoreProps = {
        BAIXO: { label: "Baixo", color: "purple" },
        MEDIO: { label: "Médio", color: "yellow" },
        ALTO: { label: "Alto", color: "cyan" },
        undefined: { label: "Sem score", color: "petroleum" }
    };
    categoryProps = {
        A: { label: "A", color: "cyan" },
        B: { label: "B", color: "yellow" },
        C: { label: "C", color: "purple" },
        D: { label: "D", color: "petroleum" },
        undefined: { label: "", color: "petroleum" }
    };
    impactProps = {
        Impactada: { label: "Impactada", color: "cyan" },
        "Sem Impacto": { label: "Sem Impacto", color: "gray" },
        undefined: { label: "Sem Impacto", color: "gray" }
    };
    sourceStatus = {
        accounting: {},
        fin: {},
        personnel: {},
        tax: {}
    };
    sourcesOutdated = {
        accounting: false,
        fin: false,
        tax: false,
        simplesNacional: false,
        personnel: false,
        eSocial: false
    };
    lastFeeFormatted = {
        t: "",
        v: 0,
        percentage: "0%"
    };
    seriePreferenceValues = {
        imposto: {},
        lucro: {},
        receita: {}
    };
    sourcesIntegrationMap = {
        accounting: false,
        tax: false,
        personnel: false
    };
    constructor(props) {
        super(props);
    }

    static restore(props, seriesConfig) {
        const entity = new CompanyEntity(props);
        entity.setIsNotEditable();
        entity.setSourceStatus();
        entity.setSoucesOutdated();
        entity.setLastFeeFormatted();
        entity.setSeriePreferenceValue(seriesConfig);
        entity.setDocumentMask();
        entity.setRelationLastUpdateViewAttrs("dominio");
        entity.setNameND();
        entity.setSourcesIntegrationMap();
        entity.setAccountingBasisOmie();
        entity.formatOMIEIntegrationName();
        return entity;
    }

    setOnlyContactsHas(accessType) {
        const contacts = this.props.contacts.filter((c) =>
            _.get(c, `relations.${accessType}`)
        );
        this.props.contactsHasRelation = contacts;
    }

    setIsNotEditable(user = new UserDataAdapter().user()) {
        const isSuper = RolesUtils.userIsInRole({ user, roles: ["Super"] });
        const isCompany = RolesUtils.userIsInRole({ user, roles: ["Empresa"] });
        this.isNotEditable =
            (this.props._id === "EMPRESA_MODELO" && !isSuper) || isCompany
                ? true
                : false;
    }

    updateMetadata(metadata) {
        if (this.props.metadata) {
            this.props.metadata = {
                ...this.props.metadata,
                ...metadata
            };
        }
    }

    updateRelation(relationName, fields) {
        if (!this.props.relation[relationName]) return;

        this.props.relation[relationName] = { ...fields };
    }

    formatOMIEIntegrationName() {
        if (!this.props.relation?.omie) return;
        const regime = {
            caixa: "Caixa",
            competencia: "Competência"
        };
        this.props.integrationName =
            regime[_.get(this.props, "relation.omie.integrationType")];
    }

    setDocumentMask() {
        this.props.documentMask = CNPJMask(this.props.document);
    }

    setCompanyIdContact() {
        if (!this.props.contacts?.length) return;
        this.props.contacts = this.props.contacts.map((c) => ({
            ...c,
            companyId: this.props._id
        }));
    }

    getIntegratioBySource(source) {
        if (["simplesNacional"].includes(source)) {
            if (_.get(this.props, "relation.serpro")) return true;

            return false;
        }

        if (["fin"].includes(source)) {
            if (this.props.organizationId) return true;
            if (_.get(this.props, "relation.contaazul")) return true;
            if (_.get(this.props, "relation.omie")) return true;

            return false;
        }

        return false;
    }

    getOMIEAppSecretMask() {
        return maskChar(
            3,
            "*",
            2,
            _.get(this.props, "relation.omie.appSecret", "")
        );
    }

    integrationStatusTooltip(system = "") {
        if (!_.get(this.props, "document")) {
            this.props.integrationTooltip = "Editar CNPJ";
        } else if (_.get(this.props, `relation.${system}.connected`)) {
            this.props.integrationTooltip = "Atualizar";
        } else if (!_.get(this.props, `relation.${system}.connected`)) {
            this.props.integrationTooltip = "Conectar";
        } else {
            this.props.integrationTooltip = "";
        }
    }

    setIsConnectedIntegration(system = "") {
        this.props.connectedSuccess =
            _.get(this.props, `relation.${system}.connected`) &&
            !_.get(this.props, `relation.${system}.withoutDeclarations`);
    }

    setOMIERelationConnected({
        address,
        integrationType,
        _id,
        tributario,
        cnae,
        cnpj,
        simplesNacional,
        razaoSocial,
        appKey,
        appSecret
    }) {
        _.set(this.props, "relation.omie", {
            _id,
            cnpj,
            razaoSocial,
            cnae,
            integrationType,
            address,
            simplesNacional,
            tributario,
            appKey,
            appSecret,
            connected: true
        });
    }

    getCompanyNiboModeled() {
        return {
            accountingId: this.props.accountingId,
            document: this.props.document,
            email: this.props.email,
            name: this.props.name,
            name_nd: this.props.name_nd,
            registered: this.props.registered,
            organizationId: this.props.organizationId,
            status: this.props.status,
            contactName: this.props.contact,
            _id: this.props._id
        };
    }

    setDefaultAccountingBasis({ system, accounting, defaultBasis = "caixa" }) {
        const integrations = { nibo: "Nibo" };

        if (!integrations[system]) return;

        const accountingBasis = accounting.getAccountingBasis(
            integrations[system]
        );
        if (
            !this.props.relation?.[system]?.accountingBasis?.default &&
            accountingBasis?.default
        ) {
            _.set(
                this.props,
                `relation.${system}.accountingBasis`,
                accountingBasis
            );
        } else if (
            !this.props.relation?.[system]?.accountingBasis?.default &&
            !accountingBasis?.default
        ) {
            _.set(
                this.props,
                `relation.${system}.accountingBasis.default`,
                defaultBasis
            );
        }
    }

    getAccountingBasis(system) {
        return this.props.relation?.[system]?.accountingBasis || {};
    }

    setLastUpdate({ integration }) {
        _.set(this.props, `relation.${integration}.lastUpdate`, new Date());
    }

    setNameND() {
        this.props.name_nd = NormalizeString.normalizeUpperCaseNd(
            this.props.name
        );
    }

    setInsightRule({ rules, insightsFastAccess, source, date, lastAvailable }) {
        const hasTriggered = rules.some(
            (rule) =>
                this._hasRule({
                    rule,
                    insightsFastAccess,
                    source,
                    date,
                    lastAvailable
                }) === 3
        );
        this.props.insightRuleAuditTotal = hasTriggered ? 0 : 1;
    }

    _hasRule({ rule, insightsFastAccess, source, date, lastAvailable }) {
        const monthFormatted = this._formatMonth(date.month);
        const dateFormatted = `${date.year}-${monthFormatted}`;

        const dateOrLastAvailable = lastAvailable
            ? "lastAvailable"
            : dateFormatted;

        const diagnosis = _.get(
            insightsFastAccess,
            `${this.props._id}-${source}-audit-${dateOrLastAvailable}-${rule._id}.diagnosis`
        );
        if (diagnosis == "critical") return 3;
    }

    _formatMonth(month) {
        return month < 9 ? `0${month + 1}` : month + 1;
    }

    setTaxationOthers() {
        this.props.taxationForm = _.get(this.props, "taxation")
            ? _.get(this.props, "taxation")
            : "Outros";
    }

    disconnectOMIE() {
        delete this.props.relation.omie;
    }

    getTotalGeneratedsLabel() {
        const totalReports = this.props.reports?.length || 0;
        const total = this.props.totalGenerateWithSuccess || 0;
        let label = ``;
        const labelSuccess = `${total} com sucesso`;
        const pendencies = totalReports - total;
        const labelPendecy = `${pendencies} pendentes`;
        if (total > 0) label = labelSuccess;
        if (pendencies > 0) {
            if (total > 0) label += ` | ${labelPendecy}`;
            else label = labelPendecy;
        }
        return label;
    }

    getReportErrorsTooltip() {
        const errors = this.props.reports.filter((r) => r.props.errorGenerate)
            .length;

        if (!errors) return "";

        return `${errors} Dash${errors > 1 ? "s" : ""} não ${
            errors > 1 ? "foram" : "foi"
        } gerado${errors > 1 ? "s" : ""} por falta de dados`;
    }

    getTotalSentsLabel(pendenciesToShare) {
        const total = this.props.totalShare || 0;
        if (total == 0 && pendenciesToShare == 0) return "Não compartilhado";
        let label = `${total} com sucesso `;
        if (pendenciesToShare > 0) {
            const totalReports = this.props.reports?.length || 0;
            const total = this.props.totalGenerateWithSuccess || 0;
            const pendenciesGenerateds = totalReports - total;
            label += `| ${pendenciesGenerateds} pendentes`;
        }

        return label;
    }

    setRelationConnected(integration, status) {
        _.set(this.props, `relation.${integration}.connected`, status);
    }

    integrationIsInProgress(integration) {
        const dateConnected = this.props.relation?.[integration]?.dateConnected;
        if (dateConnected) {
            const { days, hours } = DateUtils.elapsed(
                new Date(dateConnected),
                new Date()
            );
            if (days == 0 && hours < 24) {
                return true;
            }
        }
        const needUpdateStatus = this.props.relation?.[integration]?.needUpdate
            ?.status;
        if (needUpdateStatus === true) {
            return true;
        }
        return false;
    }

    setRelationDateConnected(integration) {
        _.set(this.props, `relation.${integration}.dateConnected`, new Date());
    }

    setRelationId(integration, _id) {
        _.set(this.props, `relation.${integration}._id`, _id);
    }

    setDominioIgnoreAnalytics(status) {
        _.set(this.props, "relation.dominio.ignoreAnalyticSeries", status);
    }

    setRelationLastUpdate(integration) {
        _.set(this.props, `relation.${integration}.lastUpdate`, new Date());
    }

    setNiboLastUpdateAt() {
        _.set(this.props, `relation.nibo.lastUpdate`, new Date());
    }

    setRelationLastUpdateViewAttrs(integration) {
        if (!_.get(this.props, `relation.${integration}.lastUpdate`)) {
            _.set(this.props, `viewAttrs.lastUpdate`, "...");
            return false;
        }

        _.set(
            this.props,
            `viewAttrs.lastUpdate`,
            DateUtils.dateToString(
                this.props.relation[integration].lastUpdate,
                "dd MMM yyyy"
            )
        );
    }

    setStatus(status) {
        this.props.status = status;
    }

    verifyIntegrationLastUpdateManual(system = "") {
        const _lastUpdate = this.props.relation?.[system]?.lastUpdateManual;
        if (!_lastUpdate) return true;
        const lastUpdate = new Date(_lastUpdate);
        const currentMonth = new Date().getUTCMonth() + 1;
        const currentYear = new Date().getUTCFullYear();
        const currentMonthRegular = String(currentMonth).padStart(2, "0");
        const current = parseInt(`${currentYear}${currentMonthRegular}`);
        const month = lastUpdate.getUTCMonth() + 1;
        const lastMonthRegular = String(month).padStart(2, "0");
        const year = lastUpdate.getUTCFullYear();
        const last = parseInt(`${year}${lastMonthRegular}`);
        if (current > last) return true;
        return false;
    }

    setRelationLastUpdateManual(integration) {
        _.set(
            this.props,
            `relation.${integration}.lastUpdateManual`,
            new Date()
        );
    }

    setRelationWithoutDeclarations(integration, status) {
        _.set(
            this.props,
            `relation.${integration}.withoutDeclarations`,
            status
        );
    }

    setRelationDescription(integration, description) {
        _.set(
            this.props,
            `relation.${integration}.description`,
            description || ""
        );
    }

    setNiboRegistered(status) {
        this.props.registered = status;
    }

    setIntegrationErrorUpdating(status, system) {
        _.set(this.props, `relation.${system}.errorUpdating`, status);
    }

    isConnectedNibo() {
        if (!this.props.registered) return false;

        if (
            this.props.registered &&
            typeof _.get(this.props, "relation.nibo") === "undefined"
        )
            return true;

        if (
            this.props.registered &&
            _.get(this.props, "relation.nibo.connected")
        )
            return true;

        if (
            this.props.registered &&
            !_.get(this.props, "relation.nibo.connected")
        )
            return false;
    }

    setScheduleDayIntegration({ system, day } = {}) {
        _.set(this.props, `relation.${system}.day`, day);
    }

    setAccountingBasisModel(system, accountingBasis) {
        this.props.relatios[system].accountingBasis = {
            default: accountingBasis
        };
    }

    getScheduleDayIntegration(system) {
        return this.props.relation?.[system]?.day;
    }

    setScheduleDefaultDayIntegration(system, day = 10) {
        if (!this.props?.relation?.[system]?.day) {
            this.setScheduleDayIntegration({ system, day });
        }
    }

    setAccountingBasis(system, accountingBasis) {
        _.set(
            this.props,
            `relation.${system}.accountingBasis`,
            accountingBasis
        );
    }

    isAccountingIntegrationType(system, accountingBasis) {
        if (this.props?.relation?.[system]?.integrationType === accountingBasis)
            return true;

        return false;
    }

    setAccountingBasisOmie(defaultValue = "caixa") {
        if (!this.props?.relation?.omie) return;
        if (!this.props.relation?.omie?.connected) {
            _.set(this.props, "relation.omie", {
                accountingBasis: {
                    default: defaultValue
                }
            });
        }
    }

    isAccountingBasisChecked(system, accountingBasis) {
        if (
            this.props?.relation?.[system]?.accountingBasis?.default ===
            accountingBasis
        )
            return true;
        if (
            this.props?.relation?.[system]?.accountingBasis?.secondary ===
            accountingBasis
        )
            return true;
    }

    hasRelation() {
        const relations = Object.keys(this.props.relation || {});
        if (relations.includes("eSocial") && relations?.length == 1) {
            return this.props.relation?.eSocial?.connected;
        }
        return relations.length ? true : false;
    }

    getRelations() {
        const relations = Object.keys(this.props.relation || {});
        const labels = {
            contaazul: "Conta Azul",
            fortes: "Fortes",
            nibo: "Nibo",
            dominio: "Dominio",
            omie: "Omie",
            serpro: "Serpro (PGDAS)",
            eSocial: "eSocial"
        };
        const relationsLabels = relations
            .map((r) => {
                if (
                    r == "eSocial" &&
                    !this.props.relation?.eSocial?.connected
                ) {
                    return "";
                }
                return labels[r] || "";
            })
            .filter(Boolean);
        return relationsLabels.join(" - ");
    }
    formatDocument() {
        this.props.documentFormated = CNPJMask(this.props.document);
    }

    setFantasyName() {
        this.props.fantasy_name = this.props.name;
    }

    setSourceStatus() {
        const sources = {
            accounting: () => {
                if (
                    !this.props?.data?.sources?.accounting?.lastImport &&
                    !this.isNotEditable
                ) {
                    this.sourceStatus.accounting = {
                        status: "import"
                    };
                } else if (
                    this.props?.data?.sources?.accounting?.needParametrize &&
                    this.props.autoAssociated?.status !== "COMPLETED" &&
                    !this.isNotEditable
                ) {
                    if (this.props.autoAssociated?.status === "PROCESSING") {
                        this.sourceStatus.accounting = {
                            status: "parametrizeInProgress"
                        };
                    } else {
                        this.sourceStatus.accounting = {
                            status: "parametrize"
                        };
                    }
                } else {
                    const date = this.props?.data?.sources?.accounting
                        ?.lastImport;
                    const dateFormatted = DateUtils.dateToString(
                        date,
                        "'até' MMM/yyyy"
                    );
                    this.sourceStatus.accounting = {
                        status: "lastDate",
                        date,
                        dateFormatted
                    };
                }
            },
            fin: () => {
                if (
                    !this.props?.data?.sources?.fin?.lastImport &&
                    !this.isNotEditable
                ) {
                    this.sourceStatus.fin = {
                        status: "import"
                    };
                } else {
                    const date = this.props?.data?.sources?.fin?.lastImport;
                    const dateFormatted = DateUtils.dateToString(
                        date,
                        "'até' MMM/yyyy"
                    );
                    this.sourceStatus.fin = {
                        status: "lastDate",
                        date,
                        dateFormatted
                    };
                }
            },
            personnel: () => {
                if (
                    this.props.relation?.eSocial?.requestsStatus === "requested"
                ) {
                    this.sourceStatus.personnel = {
                        status: "integrationInProgress"
                    };
                } else if (
                    !this.props?.data?.sources?.personnel?.lastImport &&
                    !this.isNotEditable
                ) {
                    this.sourceStatus.personnel = {
                        status: "import"
                    };
                } else {
                    const date = this.props?.data?.sources?.personnel
                        ?.lastImport;
                    const dateFormatted = DateUtils.dateToString(
                        date,
                        "'até' MMM/yyyy"
                    );
                    this.sourceStatus.personnel = {
                        status: "lastDate",
                        date,
                        dateFormatted
                    };
                }
            },
            tax: () => {
                if (
                    !this.props?.data?.sources?.tax?.lastImport &&
                    !this.isNotEditable
                ) {
                    this.sourceStatus.tax = {
                        status: "import"
                    };
                } else {
                    const date = this.props?.data?.sources?.tax?.lastImport;
                    const dateFormatted = DateUtils.dateToString(
                        date,
                        "'até' MMM/yyyy"
                    );
                    this.sourceStatus.tax = {
                        status: "lastDate",
                        date,
                        dateFormatted
                    };
                }
            }
        };
        sources["accounting"]();
        sources["fin"]();
        sources["tax"]();
        sources["personnel"]();
    }

    setSoucesOutdated() {
        const sources = [
            "accounting",
            "fin",
            "tax",
            "simplesNacional",
            "personnel",
            "eSocial"
        ];
        for (const source of sources) {
            const lastImport = this.props.data?.sources?.[source]?.lastImport;
            if (lastImport) {
                const date = new Date(lastImport);
                const { days } = DateUtils.elapsed(date, new Date());
                if (days > 60) this.sourcesOutdated[source] = true;
                else this.sourcesOutdated[source] = false;
            } else {
                this.sourcesOutdated[source] = false;
            }
        }
    }

    setSeriePreference(serie, value) {
        _.set(this.props, `performance.preferences.${serie}.mainSource`, value);
    }

    setRecordSize(size) {
        if (this.props.record) this.props.record.size = size;
    }

    setRecordTaxation(taxation) {
        if (this.props.record) this.props.record.taxation = taxation;
    }

    setRecordSegment(segment) {
        if (this.props.record) this.props.record.segment = segment;
    }

    setRecordSegmentGroup(segmentGroup) {
        if (this.props.record) this.props.record.segmentGroup = segmentGroup;
    }

    setFees(fees) {
        _.set(this.props, "performance.fees.historic", fees);
        const lastFee = _.last(fees);
        _.set(this.props, "performance.fees.lastFee", lastFee);
        this.setLastFeeFormatted();
    }

    getCustomerSinceProps() {
        const customerSince = this.props.record?.customerSince;
        const customerSinceDate = new Date(customerSince);
        if (customerSince) {
            return {
                label: customerSinceDate.getUTCFullYear(),
                date: customerSinceDate,
                month: customerSinceDate.getUTCMonth(),
                year: customerSinceDate.getUTCFullYear()
            };
        } else return {};
    }

    setLastFeeFormatted() {
        const lastFee = this.props.performance?.fees?.lastFee;
        const percentage = this.props.performance?.fees?.percentage || "0";
        if (lastFee) {
            const date = DateUtils.dateToString(
                new Date(lastFee.t),
                "MMMM yyyy"
            );
            this.lastFeeFormatted.t = date;
            this.lastFeeFormatted.v = FormatNumbers.currency(lastFee.v || 0);
            this.lastFeeFormatted.percentage = `${percentage.replace(
                ".",
                ","
            )}%`;
        }
    }

    setSeriePreferenceValue(
        seriesConfig = [
            { name: "receita", acc: false },
            { name: "imposto", acc: false },
            { name: "lucro", acc: false }
        ]
    ) {
        const series = seriesConfig;
        const preference = this.props?.performance?.series;
        for (const serie of series) {
            const serieName = serie.name;
            let source = null;
            let sourceName = null;
            const hasMainSource = this.props.performance?.preferences?.[
                serieName
            ]?.mainSource;

            const serieNameByConfig = serie.acc ? serie.acc : serieName;
            if (!hasMainSource) {
                if (preference?.[serieNameByConfig]?.simplesNacional?.value) {
                    source = preference?.[serieNameByConfig]?.simplesNacional;
                    sourceName = "simplesNacional";
                } else if (preference?.[serieNameByConfig]?.tax?.value) {
                    source = preference?.[serieNameByConfig]?.tax;
                    sourceName = "tax";
                } else if (preference?.[serieNameByConfig]?.fin) {
                    source = preference?.[serieNameByConfig]?.fin;
                    sourceName = "fin";
                } else if (preference?.[serieNameByConfig]?.accounting) {
                    source = preference?.[serieNameByConfig]?.accounting;
                    sourceName = "accounting";
                } else if (preference?.[serieNameByConfig]?.personnel) {
                    source = preference?.[serieNameByConfig]?.personnel;
                    sourceName = "personnel";
                } else if (preference?.[serieNameByConfig]?.eSocial) {
                    source = preference?.[serieNameByConfig]?.eSocial;
                    sourceName = "eSocial";
                }
            } else {
                source = preference?.[serieNameByConfig]?.[hasMainSource];
                sourceName = hasMainSource;
            }

            _.set(
                this.props,
                `performance.preferences.${serieName}.mainSource`,
                sourceName
            );

            if (source?.value) {
                const isDown = source.text?.includes("Queda");

                const isUp = source.text?.includes("Aumento");

                const polarity = serie.name.includes("imposto") ? -1 : 1; //source.polarity;

                let color = "color-gray-light";

                if ((isDown && polarity == -1) || (isUp && polarity == 1)) {
                    color = "color-cyan-dark";
                } else {
                    color = "color-purple-dark";
                }

                let icon = "fas fa-minus";

                if (isUp) {
                    icon = "fas  fa-caret-up";
                } else if (isDown) {
                    icon = "fas fa-caret-down";
                }

                this.seriePreferenceValues[serieName] = {
                    value: source.value,
                    polarity: polarity,
                    valueFormatted: FormatNumbers.byUnit(
                        "percentage",
                        source.value,
                        0
                    ),
                    tooltip: source.text,
                    variation: {
                        color,
                        icon,
                        value: FormatNumbers.currency(source.variation, 0)
                    }
                };
            } else {
                this.seriePreferenceValues[serieName] = {
                    value: 0,
                    polarity: 0,
                    valueFormatted: "0",
                    tooltip: "",
                    variation: {
                        color: "color-gray-light",
                        icon: "fas fa-minus",
                        value: FormatNumbers.currency(0, 0)
                    }
                };
            }
        }
    }

    getRelationLastUpdate(relation) {
        return (
            this.props.relation?.[relation]?.lastUpdate ||
            this.props.relation?.[relation]?.lastUpdateAutomatic ||
            this.props.lastUpdateAt?.[relation]
        );
    }

    getPropsToXLSX() {
        return {
            Empresa: this.props.name,
            CNPJ: CNPJCPFMask(this.props.document),
            Licença: this.props.status,
            Score: this.props.score,
            Categoria: this.props.category,
            Porte: this.props.size,
            "Grupo de Segmento": this.props.segmentGroup,
            CNAE: this.props.segment?.t,
            Tributação: this.props.taxation
        };
    }

    getStatusTooltip(integration) {
        if (this.props.relation?.[integration]?.description)
            return this.props.relation[integration].description;
        else if (this.props.relation?.[integration]?.connectMessage)
            return this.props.relation[integration].connectMessage;
        else if (
            this.props.relation?.[integration]?.requestsStatus === "requested"
        ) {
            return "Solicitação protocolada";
        } else if (
            this.props.relation?.[integration]?.requestsStatus === "processed"
        ) {
            return "Solicitação concluída";
        }
        return "";
    }

    setRelationRequestsStatus(integration, status) {
        _.set(this.props, `relation.${integration}.requestsStatus`, status);
    }

    getSourcesIntegrations(system) {
        if (!this.props.relation?.[system]?.sourcesIntegration) {
            return [
                {
                    source: "accounting",
                    active: true,
                    needUpdate: false
                }
            ];
        }
        return this.props.relation?.[system]?.sourcesIntegration || [];
    }

    hasSourceIntegrationUpdating(system) {
        const sourcesIntegration = this.getSourcesIntegrations(system);
        return sourcesIntegration.some((s) => s.active && s.needUpdate);
    }

    setSourcesIntegrations(system, sourcesIntegration) {
        _.set(
            this.props,
            `relation.${system}.sourcesIntegration`,
            sourcesIntegration
        );
        this.setSourcesIntegrationMap();
    }

    setSourcesIntegrationMap() {
        const sourcesIntegration = this.getSourcesIntegrations("dominio");
        sourcesIntegration.forEach((sourceIntegration) => {
            this.sourcesIntegrationMap[sourceIntegration.source] =
                sourceIntegration.active;
        });
    }

    setNeedUpdateModules(system, source, needUpdate) {
        const sourcesIntegration = this.getSourcesIntegrations(system);
        const index = sourcesIntegration.findIndex(
            (s) => s.source === source && s.active
        );
        if (index !== -1) {
            sourcesIntegration[index].needUpdate = needUpdate;
            return true;
        }
        return false;
    }

    getDataReportInteraction(key = "all") {
        if (!key) return this.props?.data?.report;

        const interactionData = this.props?.data?.report?.[key] || null;
        return interactionData;
    }

    setRelationNeedUpdate({ integration, source, start, end, date, status }) {
        if (!integration) return;
        _.set(this.props, `relation.${integration}.needUpdate`, {
            source,
            start,
            end,
            status,
            date
        });
    }

    userAccountsInactive() {
        return this.props.relation?.contaazul?.userAccountsInactive;
    }

    setUserAccountsInactive(userAccountsInactive) {
        _.set(
            this.props,
            "relation.contaazul.userAccountsInactive",
            userAccountsInactive
        );
    }

    setCopiePreference(preference, flag) {
        _.set(this.props, `${preference}`, flag);
    }
}
